import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import type {
  BuyflowCapiPurchaseIntentTriggeredProto,
  BuyflowLeadTriggeredProto,
  BuyflowPurchaseTriggeredProto,
  BuyflowViewContentTriggeredProto,
  NoomMarketingPixelEventProto,
  NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto,
  NoomMarketingPixelEventProto_SharedPropertiesProto,
} from "@noom/noom-contracts/noom_contracts/events/buyflow/marketing";
import { sendBeacon } from "@utils/fetch";
import { captureException } from "@utils/error";
import { allowCategories, LibCategories, waitForConsent } from "@utils/consent";
import { getRequestMetadata } from "@utils/meristemContext";
import { trackTask } from "@utils/monitoring/events/tasks";
import getStore from "@utils/redux/store";
import { shouldFireMedPixel } from "@utils/userSegment/features";
import { getURLParams } from "@utils/urlParams";
import { getPixelEventSourceUrl } from "./eventSourceUrl";
import { generateEventId } from "./utils";

type ClickIdStorage = {
  key: string;
  /** When set, localStorage holds `{ value, expirationDate }` and expires after this many days. */
  ttlDays?: number;
};

const SNAPCHAT_CLICK_ID: ClickIdStorage = { key: "snapchatClickId" };
const TIKTOK_CLICK_ID: ClickIdStorage = { key: "ttclid", ttlDays: 28 };
const APPLOVIN_CLICK_ID_COOKIE = "_axeid";
const MARKETING_PIXEL_CURRENCY = "USD";
const VIEW_CONTENT_VALUE = 2.0;

type MarketingPixelLeaf =
  | Pick<NoomMarketingPixelEventProto, "buyflowViewContentTriggered">
  | Pick<NoomMarketingPixelEventProto, "buyflowLeadTriggered">
  | Pick<NoomMarketingPixelEventProto, "buyflowCapiPurchaseIntentTriggered">
  | Pick<NoomMarketingPixelEventProto, "buyflowPurchaseTriggered">;

function compactAttribution(
  attribution: NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto
): NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto {
  return Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => Boolean(value))
  ) as NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto;
}

/**
 * Safari ITP can revoke localStorage (it becomes null / throws).
 */
function isLocalStorageAvailable(): boolean {
  try {
    return typeof localStorage !== "undefined" && localStorage !== null;
  } catch {
    return false;
  }
}

type StoredClickId = {
  value?: string;
  expirationDate?: string;
};

function readLocalStorageItem(key: string): string | undefined {
  if (!isLocalStorageAvailable()) {
    return undefined;
  }
  try {
    return localStorage.getItem(key) || undefined;
  } catch {
    return undefined;
  }
}

function writeLocalStorageItem(key: string, value: string) {
  if (!isLocalStorageAvailable()) {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore quota / revoked storage.
  }
}

function removeLocalStorageItem(key: string) {
  if (!isLocalStorageAvailable()) {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore revoked storage.
  }
}

function readClickId(storage: ClickIdStorage): string | undefined {
  const raw = readLocalStorageItem(storage.key);
  if (!raw || storage.ttlDays == null) {
    return raw;
  }

  try {
    const stored = JSON.parse(raw) as StoredClickId;
    const expiresAt = new Date(stored.expirationDate ?? "").getTime();
    if (!stored.value || Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
      removeLocalStorageItem(storage.key);
      return undefined;
    }
    return stored.value;
  } catch {
    // A leftover plain-string ttclid has no known age. Ignore it until a URL
    // value overwrites the key.
    return undefined;
  }
}

function persistClickId(storage: ClickIdStorage, clickId: string | undefined) {
  if (
    !clickId ||
    !isLocalStorageAvailable() ||
    clickId === readClickId(storage)
  ) {
    return;
  }

  // The URL click id is the most recent click. After C0004, overwrite any
  // older stored id so later pages without the query param still attribute.
  waitForConsent([LibCategories.targetingCookies]).then(() => {
    if (storage.ttlDays == null) {
      writeLocalStorageItem(storage.key, clickId);
      return;
    }
    // One write keeps the id and its deadline together. localStorage does not
    // expire keys, and Safari ITP cuts a 28-day cookie Max-Age to 7 days.
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + storage.ttlDays);
    writeLocalStorageItem(
      storage.key,
      JSON.stringify({
        value: clickId,
        expirationDate: expirationDate.toISOString(),
      })
    );
  });
}

/** URL value wins. Otherwise the stored id, when it has not expired. */
function clickIdFromUrlOrStorage(
  storage: ClickIdStorage,
  urlClickId: string | undefined
): string | undefined {
  persistClickId(storage, urlClickId);
  return urlClickId || readClickId(storage);
}

export function getMarketingPixelAttributionIds(): NoomMarketingPixelEventProto_AttributionIdsNestedMessageProto {
  const urlParams = getURLParams();

  return compactAttribution({
    applovinClickId: urlParams.aleid || Cookies.get(APPLOVIN_CLICK_ID_COOKIE),
    applovinAttributionToken: urlParams.alart,
    snapchatClickId: clickIdFromUrlOrStorage(
      SNAPCHAT_CLICK_ID,
      urlParams.ScCid || urlParams.scCid
    ),
    tiktokClickId: clickIdFromUrlOrStorage(TIKTOK_CLICK_ID, urlParams.ttclid),
  });
}

/** Origin only — no path, query, or hash (avoids leaking survey steps like `/survey/sex`). */
export function getMarketingPixelCurrentUrl(): string {
  return getPixelEventSourceUrl();
}

export function createMarketingPixelSharedProperties(
  userId: string
): NoomMarketingPixelEventProto_SharedPropertiesProto {
  return {
    userId,
    currentUrl: getMarketingPixelCurrentUrl(),
    clientIpAddress: getRequestMetadata().userIp,
    clientUserAgent: navigator.userAgent,
    eventTime: new Date().toISOString(),
    isMedPixel: shouldFireMedPixel(),
    isTestMode: false,
  };
}

export function createBuyflowViewContentTriggeredEvent(): BuyflowViewContentTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    currency: MARKETING_PIXEL_CURRENCY,
    contentValue: VIEW_CONTENT_VALUE,
  };
}

export function createBuyflowLeadTriggeredEvent({
  email,
}: {
  email: string;
}): BuyflowLeadTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    email,
  };
}

export function createBuyflowCapiPurchaseIntentTriggeredEvent({
  email,
  eltv,
  userId,
  productName,
}: {
  email: string;
  eltv?: number;
  userId: string;
  productName?: string;
}): BuyflowCapiPurchaseIntentTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    email,
    currency: MARKETING_PIXEL_CURRENCY,
    eltv,
    transactionId: generateEventId(userId),
    ...(productName ? { productName } : {}),
  };
}

export function createBuyflowPurchaseTriggeredEvent({
  email,
  eltv,
  transactionId,
}: {
  email: string;
  eltv?: number;
  transactionId?: string;
}): BuyflowPurchaseTriggeredProto {
  return {
    attribution: getMarketingPixelAttributionIds(),
    email,
    currency: MARKETING_PIXEL_CURRENCY,
    eltv,
    transactionId,
  };
}

async function sendNoomMarketingPixelEvent(
  taskName: string,
  leaf: MarketingPixelLeaf,
  {
    waitForTargetingConsent = false,
  }: { waitForTargetingConsent?: boolean } = {}
) {
  if (waitForTargetingConsent) {
    await waitForConsent([LibCategories.targetingCookies]);
  } else if (!allowCategories([LibCategories.targetingCookies])) {
    return;
  }

  const task = trackTask(taskName);
  try {
    task.start();
    const userId = getStore().getState().userData.user_id || "";
    await sendBeacon("/pixel/v1/i/dataStreamEventTracking/", {
      sharedProperties: {
        eventTimestamp: new Date(),
        eventUuid: uuidv4(),
      },
      noomMarketingPixelEvent: {
        sharedProperties: createMarketingPixelSharedProperties(userId),
        ...leaf,
      },
    });
    task.success();
  } catch (err) {
    captureException(err);
    task.failed(err);
  }
}

export function sendBuyflowViewContentTriggered() {
  // waitForConsent may never resolve if the user denies targeting cookies, so
  // the monitoring task must not start until after consent.
  return sendNoomMarketingPixelEvent(
    "SendBuyflowViewContentTriggeredEvent",
    { buyflowViewContentTriggered: createBuyflowViewContentTriggeredEvent() },
    { waitForTargetingConsent: true }
  );
}

export function sendBuyflowLeadTriggered({ email }: { email: string }) {
  // Immediate C0004 check. A still-pending consent drops this lead; trackLead
  // runs once, later in the buyflow, and is not retried. See pixels.md.
  return sendNoomMarketingPixelEvent("SendBuyflowLeadTriggeredEvent", {
    buyflowLeadTriggered: createBuyflowLeadTriggeredEvent({ email }),
  });
}

export function sendBuyflowCapiPurchaseIntentTriggered({
  email,
  eltv,
  userId,
  productName,
}: {
  email: string;
  eltv?: number;
  userId: string;
  productName?: string;
}) {
  return sendNoomMarketingPixelEvent(
    "SendBuyflowCapiPurchaseIntentTriggeredEvent",
    {
      buyflowCapiPurchaseIntentTriggered:
        createBuyflowCapiPurchaseIntentTriggeredEvent({
          email,
          eltv,
          userId,
          productName,
        }),
    }
  );
}

export function sendBuyflowPurchaseTriggered({
  email,
  eltv,
  transactionId,
}: {
  email: string;
  eltv?: number;
  transactionId?: string;
}) {
  return sendNoomMarketingPixelEvent("SendBuyflowPurchaseTriggeredEvent", {
    buyflowPurchaseTriggered: createBuyflowPurchaseTriggeredEvent({
      email,
      eltv,
      transactionId,
    }),
  });
}
